import type {PageProps} from 'gatsby'
import type {UnknownRecord} from 'gatsby-plugin-prismic-previews'
import {
    withPrismicUnpublishedPreview as withPrismicPreviewHOC,
    componentResolverFromMap,
} from 'gatsby-plugin-prismic-previews'
import type React from 'react'

import {prismicConfig} from '@/config/prismic-config'
import AboutTemplate from '@/templates/about/about'
import ApplePayTemplate from '@/templates/apple-pay/apple-pay'
import BillsStatusTemplate from '@/templates/bills-status/bills-status'
import BillsTrackerTemplate from '@/templates/bills-tracker/bills-tracker'
import CalendlyTemplate from '@/templates/calendly/calendly'
import CookiePolicyTemplate from '@/templates/cookie-policy/cookie-policy'
import CustomerCaseTemplate from '@/templates/customer-case/customer-case'
import CustomersTemplate from '@/templates/customers/customers'
import DemoTemplate from '@/templates/demo/demo'
import EbookTemplate from '@/templates/ebook/ebook'
import EPBTemplate from '@/templates/expense-policy-builder/expense-policy-builder'
import ERGTemplate from '@/templates/expense-report-generator/expense-report-generator'
import ExperimentalLandingPageTemplate from '@/templates/experimental-landing-page/experimental-landing-page'
import ExploreProductTemplate from '@/templates/explore-product/explore-product'
import FAQTemplate from '@/templates/faq/faq'
import FetchTemplate from '@/templates/fetch/fetch'
import FincircleTemplate from '@/templates/fincircle/fincircle'
import FlexBenefitsTemplate from '@/templates/flex-benefits/flex-benefits'
import GDPRTemplate from '@/templates/gdpr/gdpr'
import HomepageTemplate from '@/templates/homepage/homepage'
import InvoicesApprovalTemplate from '@/templates/invoices-approval/invoices-approvals'
import JobsListingTemplate from '@/templates/job-listing/job-listing'
import JobTemplate from '@/templates/jobs/job'
import LegalTemplate from '@/templates/legal/legal'
import OnDemandResourcePageTemplate from '@/templates/on-demand-resource-page/on-demand-resource-page'
import PartnerTemplate from '@/templates/partner/partner'
import PartnerBigformTemplate from '@/templates/partner-bigform/partner-bigform'
import PerksTemplate from '@/templates/perks/perks'
import PerksInternalTemplate from '@/templates/perks-internal/perks-internal'
import PodcastFormPageTemplate from '@/templates/podcast-form-page/podcast-form-page'
import PreLaunchFormPageTemplate from '@/templates/pre-launch-form-page/pre-launch-form-page'
import PricingTemplate from '@/templates/pricing/pricing'
import ResourceDetailPageTemplate from '@/templates/resource-detail-page/resource-detail-page'
import ROICalculatorTemplate from '@/templates/roi-calculator/roi-calculator'
import SlicePageTemplate from '@/templates/slice-page/slice-page'
import SubscriptionsTemplate from '@/templates/subscriptions/subscriptions'
import ThirdPartyLicencesTemplate from '@/templates/third-party-licences/third-party-licences'
import WhyPleoTemplate from '@/templates/why-pleo/why-pleo'

import type {DocTypesEnum} from './link-resolver'

/**
 * Types that reuse templates and 'not_found_page', since it uses `withprismicunpublishedpreview`
 *
 * @see here: https://prismic.io/docs/technical-reference/gatsby-plugin-prismic-previews#withprismicunpublishedpreview
 */
type ExcludedDocTypes =
    | 'not_found_page'
    | 'expense_report_generator_external_variant'
    | 'youtube_homepage_landing'

type PreviewableDocTypes = Record<Exclude<DocTypesEnum, ExcludedDocTypes>, React.ComponentType<any>>

const componentsMap: PreviewableDocTypes = {
    about_us_page: AboutTemplate,
    apple_pay_page: ApplePayTemplate,
    bills_status: BillsStatusTemplate,
    bills_tracker: BillsTrackerTemplate,
    invoices_approvals: InvoicesApprovalTemplate,
    calendly_page: CalendlyTemplate,
    customer_case: CustomerCaseTemplate,
    customers_page: CustomersTemplate,
    demo_page: DemoTemplate,
    ebook: EbookTemplate,
    expense_policy_builder: EPBTemplate,
    expense_report_generator: ERGTemplate,
    experimental_landing_page: ExperimentalLandingPageTemplate,
    explore_product: ExploreProductTemplate,
    faq_page: FAQTemplate,
    fetch_page: FetchTemplate,
    fincircle_page: FincircleTemplate,
    flex_benefits_page: FlexBenefitsTemplate,
    homepage_v3: HomepageTemplate,
    job: JobTemplate,
    job_listing: JobsListingTemplate,
    legal_page_cookie_policy: CookiePolicyTemplate,
    legal_page_gdpr: GDPRTemplate,
    legal_page_third_party_licences: ThirdPartyLicencesTemplate,
    legal_page: LegalTemplate,
    on_demand_resource_page: OnDemandResourcePageTemplate,
    partner_bigform_page: PartnerBigformTemplate,
    partner: PartnerTemplate,
    perks_internal_page: PerksInternalTemplate,
    perks_page: PerksTemplate,
    podcast_form_page: PodcastFormPageTemplate,
    pre_launch_form_page: PreLaunchFormPageTemplate,
    pricing_page: PricingTemplate,
    resource_detail_page: ResourceDetailPageTemplate,
    roi_calculator: ROICalculatorTemplate,
    slice_page: SlicePageTemplate,
    subscriptions_page: SubscriptionsTemplate,
    why_pleo_page: WhyPleoTemplate,
}

/**
 * A thin wrapper around withPrismicUnpublishedPreview, pre-filling the
 * repository info and some options, as these should always be the same.
 */
export const withPrismicUnpublishedPreview = <
    TStaticData extends UnknownRecord<PropertyKey>,
    TProps extends PageProps<TStaticData, object, unknown>
>(
    component: React.ComponentType<TProps>
) => {
    if (!prismicConfig.repositoryName) {
        throw new Error('Missing `repositoryName` in `withPrismicUnpublishedPreview`')
    }

    if (!prismicConfig.linkResolver) {
        throw new Error('Missing `linkResolver` in `withPrismicUnpublishedPreview`')
    }

    return withPrismicPreviewHOC(component, [
        {
            repositoryName: prismicConfig.repositoryName,
            linkResolver: prismicConfig.linkResolver,
            transformFieldName: prismicConfig.transformFieldName,
            componentResolver: componentResolverFromMap(componentsMap),
        },
    ])
}
